import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, makeStyles, IconButton, Tooltip } from "@material-ui/core";
import ClearIcon from "@material-ui/icons/Clear";
import RefreshIcon from "@material-ui/icons/Refresh";
import { galleryList, DEFAULT_NUM_ITEMS_PER_GALLERY_PAGE } from "../../v2_actions/gallery";
import { useForm, Controller, useWatch } from "react-hook-form";
import { Dropdown } from "../form/Dropdown";

import {
  albumDropdownOptions,
  sessionNameDropdownOptions,
  shotAtDateDropdownOptions,
  galleryEventDropdownOptions,
} from "../../v2_actions/dropdown_options";
import { useHistory } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  form: {
    width: "100%",
    padding: "10px 18px",
    background: "#f4f8fd",
    borderBottom: "1px solid #e6eef7",
    borderTop: "1px solid #e6eef7",
  },
  grayText: {
    fontWeight: "600",
    color: "#7a7a76",
    fontSize: "10px",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginRight: 2,
  },
  filterContainer: {
    alignItems: "center",
  },
  dropdownFilterStyle: {
    "& select": {
      height: 32,
      padding: "0 28px 0 10px",
      background: "#fff",
      border: "1px solid #d6e2ef",
      borderRadius: 8,
      fontSize: "12.5px",
      fontWeight: 600,
      color: "#0b3556",
      cursor: "pointer",
      transition: "border-color 0.12s, box-shadow 0.12s",
      "&:hover": {
        borderColor: "#3a86d8",
      },
      "&:focus": {
        outline: "none",
        borderColor: "#3a86d8",
        boxShadow: "0 0 0 3px #e7f0fa",
      },
    },
  },
  clearFilterButton: {
    padding: 4,
    marginTop: 0,
    width: 28,
    height: 28,
    borderRadius: 6,
    color: "#7a7a76",
    "&:hover": {
      background: "#fff",
      color: "#0b3556",
    },
  },
  refreshButton: {
    backgroundColor: "#3a86d8",
    color: "white",
    width: 32,
    height: 32,
    padding: 6,
    borderRadius: "50%",
    "&:hover": {
      backgroundColor: "#2f75c4",
    },
  },
}));

export const SideBarGalleryFilter = ({
  gallery_id,
  isFilterForPhotoArt = false,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const galleries = useSelector(() => galleryList.getVisibleObjects());

  const albumOptions = useSelector(() =>
    albumDropdownOptions.getAsOptionsWithEmptyOption({
      empty_label: "Album",
    }),
  );

  const sessionNameOptions = useSelector(() =>
    sessionNameDropdownOptions.getAsOptionsWithEmptyOption({
      empty_label: "Name",
    }),
  );
  const shotAtDateOptions = useSelector(() =>
    shotAtDateDropdownOptions.getAsOptionsWithEmptyOption({
      empty_label: "Date",
    }),
  );

  const galleryEventOptions = useSelector(() =>
    galleryEventDropdownOptions.getAsOptionsWithEmptyOption({
      empty_label: "Event",
    }),
  );

  const existingFilter = galleryList.getFilter() || {};
  const defaultValues = {
    album: existingFilter.album || "any",
    session_name: existingFilter.session_name || "any",
    shot_at_date: existingFilter.shot_at_date || "any",
    gallery_event: existingFilter.gallery_event || "any",
  };
  const formProps = useForm({ defaultValues: defaultValues });
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = formProps;

  const selectedGalleryEvent = useWatch({ control, name: "gallery_event" });
  const selectedAlbum = useWatch({ control, name: "album" });
  const prevEventRef = useRef(defaultValues.gallery_event);
  const prevAlbumRef = useRef(defaultValues.album);

  useEffect(() => {
    dispatch(galleryList.updatePaginationNumItemsPerPage(DEFAULT_NUM_ITEMS_PER_GALLERY_PAGE));

    dispatch(albumDropdownOptions.updatePaginationNumItemsPerPage(1000));
    dispatch(shotAtDateDropdownOptions.updatePaginationNumItemsPerPage(1000));
    dispatch(galleryEventDropdownOptions.updatePaginationNumItemsPerPage(1000));
    dispatch(sessionNameDropdownOptions.updatePaginationNumItemsPerPage(1000));

    const eventFilterParams = existingFilter.gallery_event && existingFilter.gallery_event !== "any"
      ? { gallery_event: existingFilter.gallery_event }
      : {};
    const nameFilterParams = { ...eventFilterParams };
    if (existingFilter.album && existingFilter.album !== "any") {
      nameFilterParams.album = existingFilter.album;
    }

    dispatch(galleryEventDropdownOptions.fetchWithFilter({}));
    dispatch(shotAtDateDropdownOptions.fetchWithFilter({}));
    dispatch(albumDropdownOptions.fetchWithFilter(eventFilterParams));
    dispatch(sessionNameDropdownOptions.fetchWithFilter(nameFilterParams));
  }, []);

  useEffect(() => {
    if (selectedGalleryEvent === prevEventRef.current) return;
    prevEventRef.current = selectedGalleryEvent;

    setValue("album", "any");
    setValue("session_name", "any");

    const filterParams = selectedGalleryEvent && selectedGalleryEvent !== "any"
      ? { gallery_event: selectedGalleryEvent }
      : {};
    dispatch(albumDropdownOptions.fetchWithFilter(filterParams));
    dispatch(sessionNameDropdownOptions.fetchWithFilter(filterParams));
  }, [selectedGalleryEvent, dispatch, setValue]);

  useEffect(() => {
    if (selectedAlbum === prevAlbumRef.current) return;
    prevAlbumRef.current = selectedAlbum;

    setValue("session_name", "any");

    const filterParams = {};
    if (selectedGalleryEvent && selectedGalleryEvent !== "any") {
      filterParams.gallery_event = selectedGalleryEvent;
    }
    if (selectedAlbum && selectedAlbum !== "any") {
      filterParams.album = selectedAlbum;
    }
    dispatch(sessionNameDropdownOptions.fetchWithFilter(filterParams));
  }, [selectedAlbum, dispatch, setValue]);

  const onFilter = (values) => {
    dispatch(galleryList.updateListFilter(values));
    dispatch(galleryList.fetchListIfNeeded());
  };

  const onFilterChanged = () => {
    handleSubmit(onFilter)();
  };

  const onClearFilters = () => {
    setValue("album", "any");
    setValue("session_name", "any");
    setValue("shot_at_date", "any");
    setValue("gallery_event", "any");
    dispatch(galleryList.clearListFilter());
    dispatch(galleryList.invalidateList());
    dispatch(galleryList.fetchListIfNeeded());
  };

  const onRefresh = () => {
    dispatch(galleryList.invalidateList());
    dispatch(galleryList.fetchListIfNeeded());
  };

  const onViewMap = () => {
    if (gallery_id) {
      history.push(`/photographer/gallery/${gallery_id}/map`);
    } else {
      history.push(`/photographer/map`);
    }
  };

  const renderAlbumFilter = () => {
    return (
      <Grid item xs={12} md={2}>

        <Controller
          name="album"
          control={control}
          render={(fieldProps) => (
            <Dropdown
              label="Album"
              fieldProps={fieldProps}
              options={albumOptions}
              onChange={onFilterChanged}
              dropdownFilterStyle={classes.dropdownFilterStyle}
            />
          )}
        />
      </Grid>
    );
  };

  const renderShotAtDateFilter = () => {
    return (
      <Grid item xs={6} md={2}>

        <Controller
          name="shot_at_date"
          control={control}
          render={(fieldProps) => (
            <Dropdown
              label="Date"
              fieldProps={fieldProps}
              onChange={onFilterChanged}
              options={shotAtDateOptions}
              dropdownFilterStyle={classes.dropdownFilterStyle}
            />
          )}
        />
      </Grid>
    );
  };

  const renderGalleryEventFilter = () => {
    return (
      <Grid item xs={6} md={2}>

        <Controller
          name="gallery_event"
          control={control}
          render={(fieldProps) => (
            <Dropdown
              label="Event"
              fieldProps={fieldProps}
              onChange={onFilterChanged}
              options={galleryEventOptions}
              dropdownFilterStyle={classes.dropdownFilterStyle}
            />
          )}
        />
      </Grid>
    );
  };

  const renderSessionNameFilter = () => {
    return (
      <Grid item xs={6} md={2}>

        <Controller
          name="session_name"
          control={control}
          render={(fieldProps) => (
            <Dropdown
              label="Name"
              fieldProps={fieldProps}
              onChange={onFilterChanged}
              options={sessionNameOptions}
              dropdownFilterStyle={classes.dropdownFilterStyle}
            />
          )}
        />
      </Grid>
    );
  };

  const renderGenreFilter = () => {
    return (
      <Grid item xs={6} md={2}>

        <Controller
          name="genre_tag"
          control={control}
          render={(fieldProps) => (
            <Dropdown
              label="Genre"
              fieldProps={fieldProps}
              onChange={onFilterChanged}
              // options={ genreNameOptions }
              dropdownFilterStyle={classes.dropdownFilterStyle}
            />
          )}
        />
      </Grid>
    );
  };

  const renderTagFilter = () => {
    return "Tag filter";
  };

  const renderGalleryNameFilter = () => {
    return "GN";
  };

  const renderForm = () => {
    return (
      <Grid container spacing={2} className={classes.filterContainer}>
        <Grid item xs={12} md={2} style={{ alignSelf: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingLeft: '5px' }}>
            <Tooltip title="Refresh galleries">
              <IconButton
                className={classes.refreshButton}
                size="small"
                onClick={onRefresh}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <span className={classes.grayText}>Filter</span>
          </div>
        </Grid>
        {renderShotAtDateFilter()}
        {!isFilterForPhotoArt && renderGalleryEventFilter()}
        {!isFilterForPhotoArt && renderAlbumFilter()}
        {isFilterForPhotoArt && renderGenreFilter()}
        {/* { isFilterForPhotoArt && renderTagFilter() } */}
        {renderSessionNameFilter()}
        <Grid item xs={6} md={1}>
  
          <Tooltip title="Clear filters">
            <IconButton
              className={classes.clearFilterButton}
              onClick={onClearFilters}
              size="small"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Grid>
        {/* { isFilterForPhotoArt && renderGalleryNameFilter() } */}
      </Grid>
    );
  };

  return (
    <form className={classes.form} onSubmit={handleSubmit(onFilter)}>
      {renderForm()}
    </form>
  );
};
